import { node, string } from 'prop-types';
import { useFlags, useLDClient, withLDConsumer } from 'launchdarkly-react-client-sdk';
import * as R from 'ramda';
import defaults from './defaults';

export const EvaluateFlag = value => {
  const fallback = process.env.REACT_APP_LAUNCH_DARKLY_FALLBACK === 'true';
  const ldEnabled = process.env.REACT_APP_LAUNCH_DARKLY === 'true';
  const ldClient = useLDClient();
  const isLaunchDarklyDown = R.isNil(ldClient) || !R.isNil(ldClient.error);
  if (isLaunchDarklyDown || !ldEnabled || !value) {
    // console.log(
    //   `LaunchDarkly is down or not initialized or no value provided. Using fallback value for flag: ${
    //     defaults[value] ?? fallback
    //   }`
    // );
    return defaults[value] ?? fallback ?? false;
  }

  const f = useFlags();

  // console.log(`Evaluating feature flag: ${value} with value: ${f[value]}`);
  return f[value] ?? defaults[value] ?? fallback ?? false;
};

/**
 * This function provides an else condition for feature flags.
 * It returns true if the feature flag is not enabled, allowing you to conditionally render components or logic when a feature flag is disabled.
 * @param {string} value - The feature flag to check.
 * @returns {boolean} - Returns true if the feature flag is not enabled, false otherwise.
 */
export const ElseFlag = value => {
  return !EvaluateFlag(value);
};

/**
 * This function filters an array or object to only include items that have the `featureFlag` property set to true, undefined, or null.
 * @param {Array|Object} arrOrObject - The array or object to filter.
 * featureFlag property is type boolean, undefined, null or string. This prop is easily paired with EvaluateFlag.
 * @returns {Array} - An array of items that pass the filter.
 */
// Example usage:
// const filteredItems = filterEnabledFeatures(myArrayOrObject);
// This will return an array of items that have the `featureFlag` property set to true, undefined, or null
// or if the featureFlag is a string, it will evaluate the flag using EvaluateFlag.
// If the input is an array, it filters the array.
// If the input is an object, it converts the object to an array of its values and filters that array.
// If featureFlag is not present, it defaults to true, allowing the item to pass through the filter.
// If featureFlag is explicitly set to false, the item will be excluded from the result.
// If featureFlag is explicitly set to true, the item will be included in the result.
// If featureFlag is undefined or null, the item will also be included in the result.
export const filterEnabledFeatures = arrOrObject => {
  const arr = Array.isArray(arrOrObject) ? arrOrObject : [arrOrObject];
  return arr.filter(item => {
    if (typeof item.featureFlag === 'string') {
      // If featureFlag is a string, we assume it is a flag name and evaluate it.
      return EvaluateFlag(item.featureFlag);
    }

    return item.featureFlag || item.featureFlag === undefined || item.featureFlag === null;
  });
};

const Flag = ({ value, children }) => {
  if (EvaluateFlag(value)) {
    return children;
  }

  return null;
};

Flag.propTypes = {
  /**
   * The children components to render if the flag is enabled.
   */
  children: node,

  /**
   * The feature flag to check.
   * This should be a string value matching the flag in LaunchDarkly.
   */
  value: string.isRequired,
};

Flag.defaultProps = {
  /**
   * Default value for children is null.
   * This means if no children are provided, nothing will be rendered.
   */
  children: null,
};

export default withLDConsumer()(Flag);
